import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  Paper,
  Button,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useSearchParams } from 'react-router-dom';

const TAB_KEYS = ['legal', 'privacidad', 'terminos', 'cookies'] as const;
type TabKey = (typeof TAB_KEYS)[number];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`legal-tabpanel-${index}`}
      aria-labelledby={`legal-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `legal-tab-${index}`,
    'aria-controls': `legal-tabpanel-${index}`,
  };
}

const LegalPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabKey | null;
  const initialTab = tabParam && TAB_KEYS.includes(tabParam) ? TAB_KEYS.indexOf(tabParam) : 0;
  const [value, setValue] = useState(initialTab);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setSearchParams({ tab: TAB_KEYS[newValue] }, { replace: true });
  };

  const handleResetCookies = () => {
    localStorage.removeItem('cookieConsent');
    window.location.reload();
  };

  const handleClose = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight="700">
          Información Legal
        </Typography>
        <Tooltip title="Cerrar">
          <IconButton aria-label="Cerrar información legal" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Card
        sx={{
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="Información legal"
            variant={isMobile ? 'scrollable' : 'fullWidth'}
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: { xs: 48, md: 64 },
              },
            }}
          >
            <Tab label="Aviso Legal" {...a11yProps(0)} />
            <Tab label="Privacidad" {...a11yProps(1)} />
            <Tab label="Términos y Condiciones" {...a11yProps(2)} />
            <Tab label="Política de Cookies" {...a11yProps(3)} />
          </Tabs>
        </Box>

        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <TabPanel value={value} index={0}>
            <Typography variant="h5" gutterBottom fontWeight="600">
              Aviso Legal
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Datos identificativos
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Titular:</strong> MISTER TECH 2025, S.L.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>NIF:</strong> B26950659
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Domicilio:</strong> C/. José Javaloyes Orts, 64 - 03206 Elche (Alicante)
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> hola@inversorhub.com
              </Typography>
            </Paper>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Actividad
              </Typography>
              <Typography variant="body1" paragraph>
                MISTER TECH 2025, S.L. se dedica a la prestación de servicios de intermediación
                tecnológica entre Personal Shoppers Inmobiliarios (PSIs) e inversores para
                oportunidades de inversión inmobiliaria.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Normativa aplicable
              </Typography>
              <Typography variant="body1" paragraph>
                Nuestra actividad se rige por:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2">
                  Ley 10/2010, de 28 de abril, de prevención del blanqueo de capitales
                </Typography>
                <Typography component="li" variant="body2">
                  Ley 5/2015, de 27 de abril, de fomento de la financiación empresarial
                </Typography>
                <Typography component="li" variant="body2">
                  Reglamento (UE) 2016/679 (RGPD)
                </Typography>
                <Typography component="li" variant="body2">
                  Ley Orgánica 3/2018, de Protección de Datos Personales
                </Typography>
                <Typography component="li" variant="body2">
                  Ley 34/2002, de Servicios de la Sociedad de la Información
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Aviso legal
              </Typography>
              <Typography variant="body1" paragraph>
                InversorHUB actúa únicamente como plataforma tecnológica de conexión entre
                inversores y Personal Shoppers Inmobiliarios (PSIs).
              </Typography>
              <Typography variant="body1" paragraph>
                No ofrecemos servicios de asesoramiento financiero, gestión de inversiones,
                intermediación en productos financieros ni custodia de fondos. Cada inversor es
                responsable de sus decisiones y de la contratación de los servicios de los PSIs.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Contacto
              </Typography>
              <Typography variant="body1">
                Para cualquier consulta relacionada con este aviso legal, puede contactar con
                nosotros en: legal@inversorhub.com
              </Typography>
            </Box>
          </TabPanel>

          <TabPanel value={value} index={1}>
            <Typography variant="h5" gutterBottom fontWeight="600">
              Política de Privacidad y RGPD
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Responsable del tratamiento
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>MISTER TECH 2025, S.L.</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                NIF: B26950659
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Dirección: C/. José Javaloyes Orts, 64 - 03206 Elche (Alicante)
              </Typography>
              <Typography variant="body2">Email: hola@inversorhub.com</Typography>
            </Paper>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Datos que recopilamos
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2">
                  <strong>Datos de identificación:</strong> Nombre, apellidos, DNI/NIE
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Datos de contacto:</strong> Email, teléfono, dirección
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Datos financieros:</strong> Información bancaria para inversiones
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Datos de navegación:</strong> Cookies, IP anonimizada, comportamiento en
                  el sitio
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Documentos:</strong> Contratos, due diligence, documentación legal
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Finalidades del tratamiento
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2">
                  Prestación del servicio de intermediación inmobiliaria
                </Typography>
                <Typography component="li" variant="body2">
                  Verificación de identidad (KYC/AML)
                </Typography>
                <Typography component="li" variant="body2">
                  Comunicaciones comerciales (con consentimiento)
                </Typography>
                <Typography component="li" variant="body2">
                  Cumplimiento de obligaciones legales
                </Typography>
                <Typography component="li" variant="body2">
                  Análisis y mejora del servicio
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Tus derechos
              </Typography>
              <Typography variant="body1" paragraph>
                Conforme al RGPD, tienes derecho a:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2">
                  <strong>Acceso:</strong> Conocer qué datos tenemos sobre ti
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Rectificación:</strong> Corregir datos inexactos
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Supresión:</strong> Eliminar tus datos
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Limitación:</strong> Restringir el tratamiento
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Portabilidad:</strong> Recibir tus datos en formato estructurado
                </Typography>
                <Typography component="li" variant="body2">
                  <strong>Oposición:</strong> Oponerte al tratamiento
                </Typography>
              </Box>

              <Paper sx={{ p: 2, mt: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <Typography variant="body2">
                  <strong>Para ejercer tus derechos:</strong> Envía un email a hola@inversorhub.com
                  con copia de tu DNI.
                </Typography>
              </Paper>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Contacto y reclamaciones
              </Typography>
              <Typography variant="body1" paragraph>
                Para cualquier consulta sobre esta política o para ejercer tus derechos:
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Email:</strong> hola@inversorhub.com
              </Typography>
              <Typography variant="body1">
                También puedes presentar una reclamación ante la Agencia Española de Protección de
                Datos (AEPD): www.aepd.es
              </Typography>
            </Box>
          </TabPanel>

          <TabPanel value={value} index={2}>
            <Typography variant="h5" gutterBottom fontWeight="600">
              Términos y Condiciones de Uso
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                1. Información general
              </Typography>
              <Typography variant="body1" paragraph>
                Estos términos regulan el uso de la plataforma InversorHub, operada por MISTER TECH
                2025, S.L., con domicilio en C/. José Javaloyes Orts, 64 - 03206 Elche (Alicante).
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                2. Objeto y ámbito
              </Typography>
              <Typography variant="body1" paragraph>
                InversorHub es una plataforma digital que facilita la conexión entre Personal
                Shoppers Inmobiliarios (PSIs) e inversores para oportunidades de inversión
                inmobiliaria.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                3. Usuarios
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                3.1 PSIs (Personal Shoppers Inmobiliarios)
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <Typography component="li" variant="body2">
                  Deben estar debidamente autorizados para ejercer la actividad
                </Typography>
                <Typography component="li" variant="body2">
                  Responsables de la veracidad de la información publicada
                </Typography>
                <Typography component="li" variant="body2">
                  Deben cumplir con la normativa de prevención de blanqueo
                </Typography>
              </Box>

              <Typography variant="subtitle1" gutterBottom>
                3.2 Inversores
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2">
                  Deben superar el test de idoneidad
                </Typography>
                <Typography component="li" variant="body2">
                  Mayores de edad con capacidad jurídica
                </Typography>
                <Typography component="li" variant="body2">
                  Residentes en territorio español
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                4. Servicios de la plataforma
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2">
                  Publicación y búsqueda de oportunidades inmobiliarias
                </Typography>
                <Typography component="li" variant="body2">
                  Herramientas de análisis y due diligence
                </Typography>
                <Typography component="li" variant="body2">
                  Gestión documental y contractual
                </Typography>
                <Typography component="li" variant="body2">
                  Sistema de pagos y seguimiento
                </Typography>
                <Typography component="li" variant="body2">
                  Reporting y métricas
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                5. Limitación de responsabilidad
              </Typography>
              <Typography variant="body1" paragraph>
                InversorHub actúa como intermediario tecnológico. No somos asesores de inversión ni
                garantizamos la rentabilidad de las operaciones. Los usuarios son responsables de
                sus decisiones de inversión.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                6. Resolución de conflictos
              </Typography>
              <Typography variant="body1" paragraph>
                Para la resolución de controversias, las partes se someten a los Juzgados y
                Tribunales de Elche (Alicante), renunciando a cualquier otro fuero que pudiera
                corresponderles.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                7. Contacto
              </Typography>
              <Typography variant="body1">
                Para consultas sobre estos términos: hola@inversorhub.com
              </Typography>
            </Box>
          </TabPanel>

          <TabPanel value={value} index={3}>
            <Typography variant="h5" gutterBottom fontWeight="600">
              Política de Cookies
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                ¿Qué son las cookies?
              </Typography>
              <Typography variant="body1" paragraph>
                Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo cuando
                visitas nuestro sitio web. Nos ayudan a mejorar tu experiencia y a entender cómo
                utilizas nuestra plataforma.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Tipos de cookies que utilizamos
              </Typography>

              <Typography variant="subtitle1" gutterBottom>
                Cookies esenciales
              </Typography>
              <Typography variant="body1" paragraph>
                Necesarias para el funcionamiento básico del sitio web. No se pueden desactivar.
              </Typography>

              <Typography variant="subtitle1" gutterBottom>
                Cookies de análisis
              </Typography>
              <Typography variant="body1" paragraph>
                Utilizamos Google Analytics con anonimización de IP y Consent Mode v2 para cumplir
                con el RGPD. Solo se activan con tu consentimiento.
              </Typography>

              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Cookies de Google Analytics:</strong>
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <Typography component="li" variant="body2">
                    `_ga`: Identificador único (2 años)
                  </Typography>
                  <Typography component="li" variant="body2">
                    `_ga_*`: Estado de la sesión (2 años)
                  </Typography>
                  <Typography component="li" variant="body2">
                    `_gid`: Identificador de sesión (24 horas)
                  </Typography>
                </Box>
              </Paper>

              <Typography variant="subtitle1" gutterBottom>
                Cookies de funcionalidad
              </Typography>
              <Typography variant="body1" paragraph>
                Permiten recordar tus preferencias y personalizar tu experiencia.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Gestión de cookies
              </Typography>
              <Typography variant="body1" paragraph>
                Puedes gestionar tus preferencias de cookies en cualquier momento. Respetamos tu
                elección y solo procesamos datos analíticos con tu consentimiento explícito.
              </Typography>

              <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Cambiar preferencias:</strong>
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Puedes modificar tu consentimiento en cualquier momento:
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleResetCookies}
                  sx={{ bgcolor: 'primary.dark' }}
                >
                  Reconfigurar cookies
                </Button>
              </Paper>

              <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Configuración del navegador:</strong>
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <Typography component="li" variant="body2">
                    Chrome: Configuración → Privacidad y seguridad → Cookies
                  </Typography>
                  <Typography component="li" variant="body2">
                    Firefox: Preferencias → Privacidad y seguridad
                  </Typography>
                  <Typography component="li" variant="body2">
                    Safari: Preferencias → Privacidad
                  </Typography>
                </Box>
              </Paper>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Contacto
              </Typography>
              <Typography variant="body1">
                Si tienes preguntas sobre nuestra política de cookies, contacta con nosotros en:
                hola@inversorhub.com
              </Typography>
            </Box>
          </TabPanel>
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      <Typography variant="body2" color="text.secondary" align="center">
        Última actualización: 15 de agosto de 2025
      </Typography>
    </Container>
  );
};

export default LegalPage;
